import { useState, useEffect, useMemo } from 'react'
import {
  Bug,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Info,
  AlertCircle,
  Trash2,
  Download,
  RefreshCw,
  Search,
  Activity
} from 'lucide-react'
import debugLogger, { LogLevel, LogEntry } from '../lib/debugLogger'
import { cn } from '../lib/utils'

/**
 * DebugPanel Component - Visual debugging interface
 * 
 * Displays real-time logs, component states, performance metrics,
 * and debugging information in a toggleable panel
 */

/**
 * Props for the DebugPanel component
 */
interface DebugPanelProps {
  defaultOpen?: boolean
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  maxHeight?: string
  className?: string
}

/**
 * Tab types for the debug panel
 */
type DebugTab = 'logs' | 'performance' | 'components' | 'summary'

/**
 * DebugPanel Component
 * Provides comprehensive visual debugging interface with real-time updates
 */
export function DebugPanel({
  defaultOpen = false,
  position = 'bottom-right',
  maxHeight = '600px',
  className
}: DebugPanelProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [activeTab, setActiveTab] = useState<DebugTab>('logs')
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [filterLevel, setFilterLevel] = useState<LogLevel | 'ALL'>('ALL')
  const [searchTerm, setSearchTerm] = useState('')
  const [autoScroll, setAutoScroll] = useState(true)
  const [isMinimized, setIsMinimized] = useState(false)

  /**
   * Subscribe to log updates from debugLogger
   */
  useEffect(() => {
    const unsubscribe = debugLogger.subscribe((updatedLogs) => {
      setLogs(updatedLogs)
    })

    // Initial load
    setLogs(debugLogger.getLogs())

    return unsubscribe
  }, [])

  /**
   * Filter logs based on level and search term
   */
  const filteredLogs = useMemo(() => {
    let filtered = logs

    // Filter by level
    if (filterLevel !== 'ALL') {
      filtered = filtered.filter(log => log.level === filterLevel)
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(log =>
        log.component.toLowerCase().includes(term) ||
        log.message.toLowerCase().includes(term) ||
        (log.data && JSON.stringify(log.data).toLowerCase().includes(term))
      )
    }

    return filtered
  }, [logs, filterLevel, searchTerm])

  /**
   * Get summary statistics
   */
  const summary = useMemo(() => debugLogger.getSummary(), [logs])

  /**
   * Get performance metrics
   */
  const performanceMetrics = useMemo(() => debugLogger.getPerformanceMetrics(), [logs])

  /**
   * Handle clear logs
   */
  const handleClearLogs = () => {
    if (confirm('Clear all debug logs?')) {
      debugLogger.clearLogs()
    }
  }

  /**
   * Handle export logs
   */
  const handleExportLogs = () => {
    const data = debugLogger.exportLogs()
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debug-logs-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  /**
   * Get log level styling
   */
  const getLogLevelStyle = (level: LogLevel) => {
    switch (level) {
      case LogLevel.ERROR:
        return 'text-status-error bg-status-error/10 border-status-error/30'
      case LogLevel.WARN:
        return 'text-status-warning bg-status-warning/10 border-status-warning/30'
      case LogLevel.INFO:
        return 'text-cyber-cyan bg-cyber-cyan/10 border-cyber-cyan/30'
      case LogLevel.DEBUG:
        return 'text-cyber-green bg-cyber-green/10 border-cyber-green/30'
      case LogLevel.TRACE:
        return 'text-text-muted bg-dark-surface/50 border-dark-border/30'
    }
  }

  /**
   * Get log level icon
   */
  const getLogLevelIcon = (level: LogLevel) => {
    switch (level) {
      case LogLevel.ERROR:
        return <AlertTriangle className="w-4 h-4" />
      case LogLevel.WARN:
        return <AlertCircle className="w-4 h-4" />
      case LogLevel.INFO:
        return <Info className="w-4 h-4" />
      case LogLevel.DEBUG:
        return <Bug className="w-4 h-4" />
      case LogLevel.TRACE:
        return <Activity className="w-4 h-4" />
    }
  }

  /**
   * Format timestamp
   */
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp)
    const timeStr = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
    const ms = date.getMilliseconds().toString().padStart(3, '0')
    return `${timeStr}.${ms}`
  }

  /**
   * Position classes
   */
  const positionClasses: Record<typeof position, string> = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4'
  }

  // Don't render in production unless explicitly enabled
  if (process.env.NODE_ENV === 'production' && !debugLogger.getConfig().enabled) {
    return null
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed z-50 p-3 rounded-cyber glass-dark border border-cyber-cyan/30',
          'hover:bg-cyber-cyan/10 hover:border-cyber-cyan hover:shadow-neon-cyan',
          'transition-all duration-300 interactive',
          positionClasses[position],
          className
        )}
        aria-label="Open debug panel"
      >
        <Bug className="w-5 h-5 text-cyber-cyan" />
      </button>
    )
  }

  return (
    <div
      className={cn(
        'fixed z-50 glass-card border-cyber-cyan/30 rounded-cyber shadow-glass-lg',
        'flex flex-col overflow-hidden',
        positionClasses[position],
        isMinimized ? 'w-80' : 'w-[600px]',
        className
      )}
      style={{ maxHeight: isMinimized ? 'auto' : maxHeight }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-border/30 bg-dark-surface/50">
        <div className="flex items-center space-x-3">
          <Bug className="w-5 h-5 text-cyber-cyan" />
          <h3 className="font-header text-lg text-cyber-cyan">Debug Panel</h3>
          <span className="text-xs text-text-muted font-mono">
            {filteredLogs.length} logs
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 rounded hover:bg-dark-panel/50 transition-colors"
            aria-label={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? (
              <ChevronUp className="w-4 h-4 text-text-secondary" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-secondary" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded hover:bg-dark-panel/50 transition-colors"
            aria-label="Close debug panel"
          >
            <X className="w-4 h-4 text-text-secondary" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Tabs */}
          <div className="flex items-center space-x-1 p-2 border-b border-dark-border/30 bg-dark-surface/30">
            {(['logs', 'performance', 'components', 'summary'] as DebugTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-3 py-1.5 rounded text-sm font-medium transition-all duration-200',
                  activeTab === tab
                    ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30'
                    : 'text-text-secondary hover:bg-dark-panel/50 hover:text-text-primary'
                )}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div className="flex items-center space-x-2 p-2 border-b border-dark-border/30 bg-dark-surface/30">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs..."
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-dark-panel/50 border border-dark-border/30 rounded focus:border-cyber-cyan/50 focus:outline-none transition-colors"
              />
            </div>

            {/* Filter */}
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value as LogLevel | 'ALL')}
              className="px-2 py-1.5 text-sm bg-dark-panel/50 border border-dark-border/30 rounded focus:border-cyber-cyan/50 focus:outline-none transition-colors"
            >
              <option value="ALL">All Levels</option>
              <option value={LogLevel.ERROR}>Errors</option>
              <option value={LogLevel.WARN}>Warnings</option>
              <option value={LogLevel.INFO}>Info</option>
              <option value={LogLevel.DEBUG}>Debug</option>
              <option value={LogLevel.TRACE}>Trace</option>
            </select>

            {/* Actions */}
            <button
              onClick={handleClearLogs}
              className="p-1.5 rounded hover:bg-dark-panel/50 transition-colors"
              title="Clear logs"
            >
              <Trash2 className="w-4 h-4 text-text-secondary" />
            </button>
            <button
              onClick={handleExportLogs}
              className="p-1.5 rounded hover:bg-dark-panel/50 transition-colors"
              title="Export logs"
            >
              <Download className="w-4 h-4 text-text-secondary" />
            </button>
            <button
              onClick={() => setLogs(debugLogger.getLogs())}
              className="p-1.5 rounded hover:bg-dark-panel/50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-text-secondary" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-2 space-y-1 bg-dark-bg/50 font-mono text-xs">
            {activeTab === 'logs' && (
              <>
                {filteredLogs.length === 0 ? (
                  <div className="text-center text-text-muted py-8">
                    No logs found
                  </div>
                ) : (
                  filteredLogs.map((log, index) => (
                    <div
                      key={index}
                      className={cn(
                        'p-2 rounded border',
                        getLogLevelStyle(log.level),
                        'hover:scale-[1.01] transition-transform duration-200'
                      )}
                    >
                      <div className="flex items-start space-x-2">
                        {getLogLevelIcon(log.level)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-text-muted">
                              {formatTimestamp(log.timestamp)}
                            </span>
                            <span className="font-semibold">[{log.component}]</span>
                            <span className="text-xs px-1 rounded bg-dark-surface/50">
                              {log.level}
                            </span>
                          </div>
                          <div className="text-text-primary break-words">
                            {log.message}
                          </div>
                          {log.data && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-cyber-cyan hover:text-cyber-magenta">
                                Show data
                              </summary>
                              <pre className="mt-2 p-2 bg-dark-surface/50 rounded text-xs overflow-x-auto">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            </details>
                          )}
                          {log.stack && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-status-error">
                                Show stack trace
                              </summary>
                              <pre className="mt-2 p-2 bg-dark-surface/50 rounded text-xs overflow-x-auto">
                                {log.stack}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === 'performance' && (
              <div className="space-y-2">
                {performanceMetrics.length === 0 ? (
                  <div className="text-center text-text-muted py-8">
                    No performance metrics recorded
                  </div>
                ) : (
                  performanceMetrics.slice(-50).reverse().map((metric, index) => (
                    <div
                      key={index}
                      className="p-2 rounded bg-dark-panel/50 border border-dark-border/30"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-cyber-magenta">
                          [{metric.component}]
                        </span>
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded',
                          metric.duration < 100
                            ? 'bg-status-success/20 text-status-success'
                            : metric.duration < 500
                            ? 'bg-status-warning/20 text-status-warning'
                            : 'bg-status-error/20 text-status-error'
                        )}>
                          {metric.duration.toFixed(2)}ms
                        </span>
                      </div>
                      <div className="text-text-secondary">
                        {metric.operation}
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {formatTimestamp(metric.timestamp)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'components' && (
              <div className="space-y-2">
                {summary.renderCounts.length === 0 ? (
                  <div className="text-center text-text-muted py-8">
                    No component data available
                  </div>
                ) : (
                  summary.renderCounts.map(([component, count]) => (
                    <div
                      key={component}
                      className="p-2 rounded bg-dark-panel/50 border border-dark-border/30"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-cyber-cyan">
                          {component}
                        </span>
                        <div className="flex items-center space-x-2">
                          <RefreshCw className="w-3 h-3 text-text-muted" />
                          <span className={cn(
                            'text-xs px-2 py-0.5 rounded',
                            count > 20
                              ? 'bg-status-warning/20 text-status-warning'
                              : 'bg-cyber-cyan/20 text-cyber-cyan'
                          )}>
                            {count} renders
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'summary' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded bg-dark-panel/50 border border-dark-border/30">
                    <div className="text-text-muted text-xs mb-1">Total Logs</div>
                    <div className="text-2xl font-bold text-cyber-cyan">
                      {summary.totalLogs}
                    </div>
                  </div>
                  <div className="p-3 rounded bg-dark-panel/50 border border-status-error/30">
                    <div className="text-text-muted text-xs mb-1">Errors</div>
                    <div className="text-2xl font-bold text-status-error">
                      {summary.errors}
                    </div>
                  </div>
                  <div className="p-3 rounded bg-dark-panel/50 border border-status-warning/30">
                    <div className="text-text-muted text-xs mb-1">Warnings</div>
                    <div className="text-2xl font-bold text-status-warning">
                      {summary.warnings}
                    </div>
                  </div>
                  <div className="p-3 rounded bg-dark-panel/50 border border-dark-border/30">
                    <div className="text-text-muted text-xs mb-1">Components</div>
                    <div className="text-2xl font-bold text-cyber-magenta">
                      {summary.components}
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded bg-dark-panel/50 border border-dark-border/30">
                  <div className="text-text-muted text-xs mb-2">Active Components</div>
                  <div className="flex flex-wrap gap-1">
                    {summary.componentList.map(comp => (
                      <span
                        key={comp}
                        className="px-2 py-1 text-xs rounded bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30"
                      >
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-3 rounded bg-dark-panel/50 border border-dark-border/30">
                  <div className="text-text-muted text-xs mb-2">Performance Metrics</div>
                  <div className="text-sm text-text-primary">
                    {summary.performanceMetrics} operations tracked
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-2 border-t border-dark-border/30 bg-dark-surface/30 text-xs text-text-muted">
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="rounded border-dark-border/30"
                />
                <span>Auto-scroll</span>
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Activity className="w-3 h-3 text-status-success animate-pulse" />
              <span>Live</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default DebugPanel